import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEmployee } from '@/features/employees/hooks/use-employee';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/atoms/avatar';
import { ArrowLeft, Edit, Mail, Phone, Shield, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDate } from '@/lib/utils';
import type { EmployeeStatus, EmployeeRole } from '@/features/employees/types/employees.types';

const statusVariantMap: Record<EmployeeStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  active: 'default',
  inactive: 'secondary',
  pending: 'outline',
};

const roleColors: Record<EmployeeRole, string> = {
  manager: 'bg-purple-100 text-purple-800',
  cashier: 'bg-blue-100 text-blue-800',
  inventory: 'bg-green-100 text-green-800',
  support: 'bg-orange-100 text-orange-800',
};

export default function EmployeeDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: employee, isLoading, error } = useEmployee(id ?? '');

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('employees.error')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/employees">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </Link>
          <Avatar className="h-12 w-12 shrink-0">
            <AvatarImage src={employee.avatar} />
            <AvatarFallback>
              {(employee.firstName?.[0] || '')}{(employee.lastName?.[0] || '')}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold truncate">{employee.firstName} {employee.lastName}</h1>
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${roleColors[employee.role]}`}>
              {t(`employees.roles.${employee.role}`)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={statusVariantMap[employee.status]}>
            {t(`employees.status.${employee.status}`)}
          </Badge>
          <Link to={`/employees/${employee.id}/edit`}>
            <Button variant="outline" size="sm">
              <Edit className="size-4 mr-2" />
              {t('common.edit')}
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('employees.contact')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="size-4 text-muted-foreground" />
              <a href={`mailto:${employee.email}`} className="hover:underline">{employee.email}</a>
            </div>
            {employee.phone && (
              <div className="flex items-center gap-3">
                <Phone className="size-4 text-muted-foreground" />
                <a href={`tel:${employee.phone}`} className="hover:underline">{employee.phone}</a>
              </div>
            )}
            {employee.lastLoginAt && (
              <div className="flex items-center gap-3">
                <Clock className="size-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t('employees.lastLogin')}: {formatDate(employee.lastLoginAt)}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="size-5" />
              {t('employees.permissions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {employee.permissions && employee.permissions.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {employee.permissions.map((perm) => (
                  <Badge key={perm} variant="outline">
                    {perm}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('employees.noPermissions')}</p>
            )}
          </CardContent>
        </Card>

        {employee.shopIds && employee.shopIds.length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>{t('employees.assignedShops')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {employee.shopIds.map((shopId) => (
                  <Link key={shopId} to={`/shops/${shopId}`}>
                    <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80">
                      {shopId}
                    </Badge>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
